import * as React from 'react';
import { Datagrid, FunctionField, List, useListContext, useNotify } from 'react-admin';
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/page-header/PageHeader';
import { EmptyBox } from '../../components/empty-box/EmptyBox';
import { listSurfaceSx } from '../../core/theme/listStyles';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { isAdminRole } from '../../utils/roles';
import { useAccessibleLocations } from '../../hooks/useAccessibleLocations';
import {
  defaultOrderHistoryFilters,
  formatAmount,
  formatDateTime,
  HISTORY_STATUSES,
  ORDER_TYPES,
  OrderHistoryFilterValues,
  OrderHistoryListItem,
  OrderHistoryStatus,
} from './orderHistoryTypes';

export const OrderStatusChip = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const done = status === 'DELIVERED';
  const fresh = status === 'PAID';
  return (
    <Chip
      size="small"
      label={t(`orderHistory.status.${status}`, { defaultValue: status })}
      sx={{
        backgroundColor: done
          ? 'var(--color-success-soft)'
          : fresh
            ? 'var(--color-primary-soft)'
            : 'var(--color-bg-muted)',
        color: done
          ? 'var(--color-success)'
          : fresh
            ? 'var(--color-primary)'
            : 'var(--color-text-secondary)',
        flexShrink: 0,
      }}
    />
  );
};

const filterControlSx = { minWidth: 160, flex: { xs: '1 1 100%', sm: '0 1 auto' } };

/**
 * Pasek filtrow nad lista. Wartosci trzyma react-admin (filterValues), dataProvider zamienia
 * dni na zakres chwil (toOrderHistoryQuery).
 */
const OrderHistoryFilters = ({ isAdmin }: { isAdmin: boolean }) => {
  const { t } = useTranslation();
  const { filterValues, setFilters } = useListContext();
  const { locations } = useAccessibleLocations();
  const values = filterValues as OrderHistoryFilterValues;
  const [number, setNumber] = React.useState(values.q ?? '');

  const update = (patch: Partial<OrderHistoryFilterValues>, debounce = false) =>
    setFilters({ ...values, ...patch }, undefined, debounce);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 'var(--space-3)',
        px: PAGE_GUTTER,
        pb: 'var(--space-4)',
      }}
    >
      <TextField
        type="date"
        size="small"
        label={t('orderHistory.filters.dateFrom')}
        value={values.dateFrom ?? ''}
        onChange={(e) => update({ dateFrom: e.target.value })}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={filterControlSx}
      />
      <TextField
        type="date"
        size="small"
        label={t('orderHistory.filters.dateTo')}
        value={values.dateTo ?? ''}
        onChange={(e) => update({ dateTo: e.target.value })}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={filterControlSx}
      />
      <FormControl size="small" sx={filterControlSx}>
        <InputLabel shrink>{t('orderHistory.filters.status')}</InputLabel>
        <Select
          displayEmpty
          label={t('orderHistory.filters.status')}
          value={values.status ?? ''}
          onChange={(e) => update({ status: e.target.value as OrderHistoryStatus | '' })}
        >
          <MenuItem value="">{t('orderHistory.filters.allStatuses')}</MenuItem>
          {HISTORY_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {t(`orderHistory.status.${status}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={filterControlSx}>
        <InputLabel shrink>{t('orderHistory.filters.orderType')}</InputLabel>
        <Select
          displayEmpty
          label={t('orderHistory.filters.orderType')}
          value={values.orderType ?? ''}
          onChange={(e) =>
            update({ orderType: e.target.value as OrderHistoryFilterValues['orderType'] })
          }
        >
          <MenuItem value="">{t('orderHistory.filters.allTypes')}</MenuItem>
          {ORDER_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`orderHistory.orderType.${type}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isAdmin && (
        <FormControl size="small" sx={filterControlSx}>
          <InputLabel shrink>{t('orderHistory.filters.location')}</InputLabel>
          <Select
            displayEmpty
            label={t('orderHistory.filters.location')}
            value={values.locationId ?? ''}
            onChange={(e) => {
              const value = e.target.value as number | '';
              update({ locationId: value === '' ? '' : Number(value) });
            }}
          >
            <MenuItem value="">{t('orderHistory.filters.allLocations')}</MenuItem>
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <TextField
        size="small"
        label={t('orderHistory.filters.orderNumber')}
        placeholder="0042"
        value={number}
        onChange={(e) => {
          setNumber(e.target.value);
          update({ q: e.target.value }, true);
        }}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={filterControlSx}
      />
    </Box>
  );
};

// Pusty wynik przy aktywnych filtrach - pasek filtrow zostaje nad komunikatem.
const NoOrders = () => {
  const { t } = useTranslation();
  return (
    <EmptyBox
      title={t('orderHistory.empty.title')}
      description={t('orderHistory.empty.description')}
      showCreate={false}
    />
  );
};

const MobileOrderHistoryList = () => {
  const { data, isLoading } = useListContext<OrderHistoryListItem>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  if (isLoading || !data) return null;
  if (data.length === 0) return <NoOrders />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((order) => (
        <Box
          key={order.id}
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/orderHistory/${order.id}/show`)}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/orderHistory/${order.id}/show`)}
          sx={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            p: 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            cursor: 'pointer',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: 'var(--text-sm)', fontFamily: 'monospace', flex: 1 }}
            >
              #{order.orderNumber}
            </Typography>
            <OrderStatusChip status={order.status} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 'var(--space-4)',
              rowGap: 'var(--space-1)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <span>{formatDateTime(order.createdAt, i18n.language)}</span>
            <span>{t(`orderHistory.orderType.${order.orderType}`)}</span>
            <span>{order.customerName ?? '—'}</span>
            <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
              {formatAmount(order.totalPrice, order.currency, i18n.language)}
            </span>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const DesktopOrderHistoryList = ({ isAdmin }: { isAdmin: boolean }) => {
  const { t, i18n } = useTranslation();

  return (
    <Datagrid
      bulkActionButtons={false}
      rowClick="show"
      empty={<NoOrders />}
      sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700, letterSpacing: 0.2 } }}
    >
      <FunctionField
        source="orderNumber"
        label={t('orderHistory.columns.orderNumber')}
        render={(o: OrderHistoryListItem) => (
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 'var(--text-sm)' }}>
            #{o.orderNumber}
          </Typography>
        )}
      />
      <FunctionField
        source="createdAt"
        label={t('orderHistory.columns.createdAt')}
        render={(o: OrderHistoryListItem) => formatDateTime(o.createdAt, i18n.language)}
      />
      {isAdmin && (
        <FunctionField
          sortable={false}
          label={t('orderHistory.columns.location')}
          render={(o: OrderHistoryListItem) => o.locationName}
        />
      )}
      <FunctionField
        sortable={false}
        label={t('orderHistory.columns.customer')}
        render={(o: OrderHistoryListItem) => o.customerName ?? '—'}
      />
      <FunctionField
        sortable={false}
        label={t('orderHistory.columns.orderType')}
        render={(o: OrderHistoryListItem) => t(`orderHistory.orderType.${o.orderType}`)}
      />
      <FunctionField
        sortable={false}
        label={t('orderHistory.columns.paymentType')}
        render={(o: OrderHistoryListItem) => t(`orderHistory.paymentType.${o.paymentType}`)}
      />
      <FunctionField
        sortable={false}
        label={t('orderHistory.columns.status')}
        render={(o: OrderHistoryListItem) => <OrderStatusChip status={o.status} />}
      />
      <FunctionField
        source="totalPrice"
        label={t('orderHistory.columns.total')}
        textAlign="right"
        render={(o: OrderHistoryListItem) => (
          <Typography sx={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>
            {formatAmount(o.totalPrice, o.currency, i18n.language)}
          </Typography>
        )}
      />
    </Datagrid>
  );
};

export const OrderHistoryList = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isAdmin = isAdminRole();
  // Liczone raz na wejscie na ekran: "ostatnie 7 dni" od dzisiaj.
  const defaults = React.useMemo(defaultOrderHistoryFilters, []);

  return (
    <>
      <PageHeader title={t('resources.orderHistory')} subtitle={t('orderHistory.subtitle')} />
      <List
        resource="orderHistory"
        actions={false}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
        filterDefaultValues={defaults}
        // empty={false}: domyslny ekran "brak danych" zastapilby tez pasek filtrow.
        empty={false}
        queryOptions={{
          // Bledy BE ({ error: "..." }) - np. zakres dluzszy niz 366 dni.
          onError: (error: any) => notify(error?.body?.error ?? error?.message, { type: 'error' }),
        }}
        sx={listSurfaceSx({ isMobile })}
      >
        <OrderHistoryFilters isAdmin={isAdmin} />
        {isMobile ? <MobileOrderHistoryList /> : <DesktopOrderHistoryList isAdmin={isAdmin} />}
      </List>
    </>
  );
};

export default OrderHistoryList;
